import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import { ConfigProvider, Layout, Button, Typography } from 'antd';
import { LogoutOutlined } from '@ant-design/icons';
import Login from './components/LoginNew';
import HotelGroupManagement from './components/HotelGroupManagement';
import HotelManagement from './components/HotelManagement';
import HotelDataDashboard from './components/HotelDataDashboard';

const { Header } = Layout;
const { Title } = Typography;

// Protected Route wrapper
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = !!localStorage.getItem('token');
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

// Hotel Groups Page
const HotelGroupsPage: React.FC = () => {
  const navigate = useNavigate();

  const handleSelectGroup = (groupId: number, groupName: string) => {
    navigate(`/groups/${groupId}/hotels`);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{
        background: '#fff',
        padding: '0 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}>
        <Title level={3} style={{ margin: 0 }}>PMS Data Management</Title>
        <Button
          danger
          icon={<LogoutOutlined />}
          onClick={handleLogout}
        >
          Logout
        </Button>
      </Header>
      <div style={{ flex: 1 }}>
        <HotelGroupManagement onSelectGroup={handleSelectGroup} />
      </div>
    </Layout>
  );
};

// Hotels Page
const HotelsPage: React.FC = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const [groupName, setGroupName] = useState('');

  const handleSelectHotel = (hotelId: number, hotelName: string) => {
    navigate(`/groups/${groupId}/hotels/${hotelId}/data`);
  };

  const handleBack = () => {
    navigate('/groups');
  };

  return (
    <HotelManagement
      groupId={Number(groupId)}
      groupName={groupName}
      onBack={handleBack}
      onSelectHotel={handleSelectHotel}
      onGroupNameLoaded={setGroupName}
    />
  );
};

// Hotel Data Page
const HotelDataPage: React.FC = () => {
  const { groupId, hotelId, tab } = useParams<{ groupId: string; hotelId: string; tab?: string }>();
  const navigate = useNavigate();
  const [groupName, setGroupName] = useState('');
  const [hotelName, setHotelName] = useState('');

  const handleBack = () => {
    navigate(`/groups/${groupId}/hotels`);
  };

  const handleBackToGroups = () => {
    navigate('/groups');
  };

  const handleTabChange = (tabKey: string) => {
    navigate(`/groups/${groupId}/hotels/${hotelId}/data/${tabKey}`, { replace: true });
  };

  return (
    <HotelDataDashboard
      hotelId={Number(hotelId)}
      hotelName={hotelName}
      groupName={groupName}
      groupId={Number(groupId)}
      activeTab={tab}
      onBack={handleBack}
      onBackToGroups={handleBackToGroups}
      onTabChange={handleTabChange}
      onNamesLoaded={(hotel, group) => {
        setHotelName(hotel);
        setGroupName(group);
      }}
    />
  );
};

// Login Page
const LoginPage: React.FC = () => {
  const navigate = useNavigate();

  const handleLogin = () => {
    navigate('/groups');
  };

  return <Login onLogin={handleLogin} />;
};

function App() {
  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#667eea',
          borderRadius: 6,
        },
      }}
    >
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/groups" element={<ProtectedRoute><HotelGroupsPage /></ProtectedRoute>} />
          <Route path="/groups/:groupId/hotels" element={<ProtectedRoute><HotelsPage /></ProtectedRoute>} />
          <Route path="/groups/:groupId/hotels/:hotelId/data/:tab?" element={<ProtectedRoute><HotelDataPage /></ProtectedRoute>} />
          <Route path="/" element={<Navigate to="/groups" replace />} />
        </Routes>
      </BrowserRouter>
    </ConfigProvider>
  );
}

export default App;
